/**
 * Locale-tolerant money parser.
 *
 * Problem: `parseFloat("1148,34")` returns 1148 in JS — cents silently lost.
 * Samuel hit this on PO177 by pasting a DE-formatted shipping amount into the
 * form. The PO total then read 3.181,60 instead of 3.181,94. See
 * docs/backlog-auto-repair/2026-04-24-po-3-decimal-precision.md.
 *
 * Rules (currency-driven, DE-primary since brewout.de operates in EUR):
 *
 *   Both `.` and `,` present  → the RIGHTMOST character is the decimal
 *                               separator (handles "3.181,94" AND "3,181.94").
 *
 *   Single separator         → currency decides:
 *     EUR (DE) default:
 *       comma only              → decimal         ("0,439" → 0.439)
 *       dot only, 1-2 digits    → decimal         ("42.5", "42.53")
 *       dot only, exactly 3     → thousands       ("1.234" → 1234)
 *       dot only, 4+ digits     → decimal         ("1.2345")
 *     USD / GBP / CHF:
 *       dot only                → decimal
 *       comma only, 1-2 digits  → decimal         ("0,5")
 *       comma only, exactly 3   → thousands       ("1,234" → 1234)
 *       comma only, 4+ digits   → decimal
 *
 *   No-decimal currencies (JPY/KRW) → treat any separator as thousands,
 *                                     parse + round to integer.
 *
 * Three-decimal entry falls out naturally: EUR user types "0,439", USD user
 * types "0.439" — both parse to 0.439.
 *
 * Returns `null` for unparseable / empty input. Callers decide how to handle
 * null (most should preserve the raw string until the user commits).
 */

const DOT_DECIMAL_CURRENCIES = new Set(['USD', 'GBP', 'CHF', 'AUD', 'CAD', 'NZD', 'HKD', 'SGD']);
const NO_DECIMAL_CURRENCIES = new Set(['JPY', 'KRW', 'VND', 'CLP', 'HUF', 'IDR']);

export function parseMoney(input, currency = 'EUR') {
  if (input === null || input === undefined) return null;
  if (typeof input === 'number') return Number.isFinite(input) ? input : null;

  let s = String(input).trim();
  if (!s) return null;

  // Strip any character that isn't a digit, separator, or leading minus.
  // (Currency symbols, spaces, non-breaking spaces, letters all get dropped.)
  const negative = /^-/.test(s);
  s = s.replace(/[^\d.,]/g, '');
  if (!s) return null;

  const cur = (currency || 'EUR').toUpperCase();

  if (NO_DECIMAL_CURRENCIES.has(cur)) {
    const digitsOnly = s.replace(/[.,]/g, '');
    const n = parseInt(digitsOnly, 10);
    if (Number.isNaN(n)) return null;
    return negative ? -n : n;
  }

  const lastDot = s.lastIndexOf('.');
  const lastComma = s.lastIndexOf(',');

  // No separator at all → plain integer string.
  if (lastDot === -1 && lastComma === -1) {
    const n = parseInt(s, 10);
    if (Number.isNaN(n)) return null;
    return negative ? -n : n;
  }

  // Both separators present → rightmost wins as decimal sep.
  if (lastDot !== -1 && lastComma !== -1) {
    if (lastComma > lastDot) {
      s = s.replace(/\./g, '').replace(',', '.');
    } else {
      s = s.replace(/,/g, '');
    }
    const n = parseFloat(s);
    if (Number.isNaN(n)) return null;
    return negative ? -n : n;
  }

  // Exactly one kind of separator. Apply currency-aware heuristic.
  const sep = lastDot !== -1 ? '.' : ',';
  const sepCount = s.split(sep).length - 1;
  const digitsAfter = s.length - (lastDot !== -1 ? lastDot : lastComma) - 1;

  const dotIsDecimal = DOT_DECIMAL_CURRENCIES.has(cur);
  const commaIsDecimal = !dotIsDecimal;

  const sepIsDecimalByLocale =
    (sep === ',' && commaIsDecimal) || (sep === '.' && dotIsDecimal);

  let decimal;
  if (sepCount > 1) {
    // Multiple instances of the same separator → thousands grouping.
    decimal = false;
  } else if (sepIsDecimalByLocale) {
    decimal = true;
  } else {
    // Opposite-locale separator, single occurrence.
    // Ambiguous: "1.234" in EUR, "1,234" in USD. Use digit-count heuristic:
    //   exactly 3 digits after → thousands grouping
    //   anything else → decimal (user's intent)
    decimal = digitsAfter !== 3;
  }

  if (decimal) {
    s = s.replace(sep, '.');
  } else {
    s = s.split(sep).join('');
  }

  const n = parseFloat(s);
  if (Number.isNaN(n)) return null;
  return negative ? -n : n;
}

/**
 * Same as parseMoney, but returns the original input unchanged when parsing
 * fails. Useful in onChange handlers that want to hold partially-typed values
 * (e.g. "1," mid-typing) without flipping to null.
 */
export function parseMoneyOrRaw(input, currency = 'EUR') {
  const n = parseMoney(input, currency);
  return n === null ? input : n;
}
